import React from "react";

import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Error from "Components/Error";
import Button, { Link } from "ds/Button";
import useInput from "Hooks/useInput";
import { capitalize } from "Libs/utils";
import { useAppSelector } from "Store/hooks";

import IntegrationIcon from "../IntegrationIcon";

import { newRelicFormConfig } from "./config";
import CostInfo from "./CostInfo";
import { getInitialValue } from "./helper";
import { getFormInputComp } from "./inputs";
import * as S from "./IntegrationForm.styles";
import { IntegrationFormProps } from "./type";

const NewRelick = ({
  onCancel,
  onRemove,
  onSubmit,
  integration
}: IntegrationFormProps) => {
  const canDelete = integration ? integration?.hasLink("#delete") : true;

  const { error, errorMessage } = useAppSelector(
    ({ integration: { error, errorMessage } }) => ({
      error,
      errorMessage
    })
  );

  const loading = useAppSelector(state => state.integration.loading);
  const status = useAppSelector(state => state.integration.status);
  const intl = useIntl();
  const params = useParams<{ type: string }>();

  const type = integration ? integration.type : params.type;

  const { inputs, handleInputChange } = useInput(
    getInitialValue(newRelicFormConfig, integration)
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(inputs, type);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    onCancel?.();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    onRemove?.();
  };

  return (
    <S.Form onSubmit={handleSubmit}>
      <S.Header>
        <IntegrationIcon type={type} />
        <S.Title tag="h2">
          {intl.formatMessage({
            id: `integration.type.${type}`,
            defaultMessage: type
          })}
        </S.Title>
      </S.Header>
      <S.Description>
        {intl.formatMessage(
          {
            id: "integration.log_forwarding_form_title"
          },
          { type }
        )}
        &nbsp;
        <Link
          analyticId="cancel"
          href={intl.formatMessage({
            id: "links.log_forwarding_documentation"
          })}
        >
          {intl.formatMessage({
            id: "see_our_documentation"
          })}
        </Link>
      </S.Description>
      {errorMessage && <Error>{errorMessage}</Error>}
      <S.InputFieldWrapper>
        {newRelicFormConfig.map(config => {
          const Field = getFormInputComp(config);
          return (
            <div className="input-field" key={`${config.id}-${type}`}>
              <Field
                value={inputs[config.id]}
                onChange={handleInputChange}
                width="100%"
                error={error?.detail?.[config.id]}
                {...config}
              />
            </div>
          );
        })}
      </S.InputFieldWrapper>
      <CostInfo />
      <S.ActionButtonsContainer isEdit={!!integration}>
        <Button
          id="save"
          aria-label={intl.formatMessage({
            id: integration ? "save" : "integration.add"
          })}
          type="submit"
          analyticId={integration ? "save" : "integration.add"}
          disabled={status === "pending"}
        >
          {capitalize(
            intl.formatMessage({
              id: integration ? "save" : "integration.add"
            })
          )}
        </Button>

        {onCancel && (
          <Button
            analyticId="cancel"
            id="cancel"
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
            aria-label={intl.formatMessage({ id: "cancel" })}
          >
            {capitalize(intl.formatMessage({ id: "cancel" }))}
          </Button>
        )}
        {onRemove && canDelete && (
          <Button
            id="remove"
            variant="outline"
            onClick={handleRemove}
            disabled={loading}
            analyticId="remove"
            aria-label={intl.formatMessage({ id: "remove" })}
          >
            {capitalize(intl.formatMessage({ id: "remove" }))}
          </Button>
        )}
      </S.ActionButtonsContainer>
    </S.Form>
  );
};

export default NewRelick;
